import { useState, useCallback, useMemo } from 'react';
import { FolderPermissionsModal } from 'containers/pages/users/components/folderPermissions';

/**
 * @hook useFolderPermissionsIntegration
 * @description Custom hook that encapsulates all folder permissions logic for UserInfoForm
 * 
 * BENEFITS:
 * - Separates folder permissions logic from UI components
 * - Provides clean interface for parent components
 * - Handles mode detection automatically
 * - Manages modal state and callbacks
 * - Reduces complexity in UserInfoForm
 * 
 * @param {Object} config Configuration object
 * @param {string} config.mode - "create" or "edit" mode (optional, auto-detected)
 * @param {number|null} config.userId - User ID for edit mode
 * @param {function} config.onPermissionsSet - Callback for create mode (draft permissions)
 * @param {function} config.onAutoSave - Callback for edit mode (auto-save complete)
 * @returns {Object} Hook interface with modal, handlers, and state
 */
export const useFolderPermissionsIntegration = ({
  mode,
  userId,
  onPermissionsSet,
  onAutoSave
}) => {
  const [modalOpen, setModalOpen] = useState(false);
  const [draftPermissions, setDraftPermissions] = useState([]);

  /**
   * Determine the mode automatically if not provided
   */
  const resolvedMode = useMemo(() => {
    if (mode) return mode;
    return userId ? "edit" : "create";
  }, [mode, userId]);

  /**
   * Handle opening the folder permissions modal
   */
  const handleOpenModal = useCallback(() => {
    setModalOpen(true);
  }, []);

  /**
   * Handle closing the folder permissions modal
   */
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
  }, []);

  /**
   * Handle setting draft permissions (create mode)
   */
  const handleSetDraftPermissions = useCallback((permissions) => {
    setDraftPermissions(permissions);
    if (onPermissionsSet) {
      onPermissionsSet(permissions);
    }
  }, [onPermissionsSet]);

  /**
   * Handle auto-save completion (edit mode)
   */
  const handleAutoSaveComplete = useCallback(() => {
    if (onAutoSave) {
      onAutoSave();
    }
  }, [onAutoSave]);

  /**
   * Render the folder permissions modal
   * Only renders when modalOpen is true to prevent unnecessary API calls
   */
  const modal = useMemo(() => (
    modalOpen ? (
      <FolderPermissionsModal
        open={modalOpen}
        onClose={handleCloseModal}
        mode={resolvedMode}
        userId={userId}
        onPermissionsSet={handleSetDraftPermissions}
        onAutoSave={handleAutoSaveComplete}
      />
    ) : null
  ), [
    modalOpen,
    handleCloseModal,
    resolvedMode,
    userId,
    handleSetDraftPermissions,
    handleAutoSaveComplete
  ]);

  return {
    // State
    mode: resolvedMode,
    draftPermissions,
    modalOpen,
    
    // Handlers
    handleOpenModal,
    handleCloseModal,
    handleSetDraftPermissions,
    handleAutoSaveComplete,
    
    // Components
    modal
  };
};

export default useFolderPermissionsIntegration;
